import React from 'react';
import { motion } from 'framer-motion';
import { Cpu, Zap, Shield, GitBranch, Layers, Activity } from 'lucide-react';

const Features = () => {
    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.1,
                delayChildren: 0.3
            }
        }
    };

    const itemVariants = {
        hidden: { y: 20, opacity: 0 },
        visible: {
            y: 0,
            opacity: 1,
            transition: { type: 'spring', stiffness: 100 }
        }
    };

    const features = [
        {
            icon: <Cpu className="w-8 h-8 text-neon-blue" />,
            title: "Neural Processing",
            description: "Advanced algorithms that optimize pipeline execution with AI-driven predictive scheduling."
        },
        {
            icon: <GitBranch className="w-8 h-8 text-neon-purple" />,
            title: "Branch Logic",
            description: "Complex decision trees visualized as cybernetic flowcharts for intuitive logic management."
        },
        {
            icon: <Shield className="w-8 h-8 text-green-400" />,
            title: "Quantum Encryption",
            description: "Military-grade security protocols ensuring your data remains impervious to external threats."
        },
        {
            icon: <Zap className="w-8 h-8 text-yellow-400" />,
            title: "Hyper-Speed Execution",
            description: "Zero-latency runtime environment compiled to WebAssembly for near-native performance."
        },
        {
            icon: <Layers className="w-8 h-8 text-pink-500" />,
            title: "Multi-Layer Abstraction",
            description: "Seamlessly switch between high-level visual editing and low-level code injection."
        },
        {
            icon: <Activity className="w-8 h-8 text-cyan-400" />,
            title: "Real-time Telemetry",
            description: "Live monitoring of pipeline health with holographic visualization of data streams."
        }
    ];

    return (
        <div className="min-h-screen bg-cyber-gray text-white font-rajdhani overflow-hidden relative">
            {/* Background Grid Animation */}
            <div className="absolute inset-0 z-0 opacity-10 pointer-events-none">
                <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px]"></div>
            </div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-20 relative z-10">
                <motion.div
                    initial={{ opacity: 0, y: -50 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, ease: "easeOut" }}
                    className="text-center mb-20"
                >
                    <span className="text-neon-blue font-mono tracking-widest text-sm uppercase mb-4 block">System Capabilities</span>
                    <h1 className="text-5xl md:text-7xl font-bold font-orbitron bg-clip-text text-transparent bg-gradient-to-r from-neon-blue via-white to-neon-purple mb-6">
                        CORE FEATURES
                    </h1>
                    <p className="text-xl text-gray-400 max-w-2xl mx-auto leading-relaxed">
                        Deploying next-generation infrastructure for autonomous pipeline management.
                    </p>
                </motion.div>

                <motion.div
                    variants={containerVariants}
                    initial="hidden"
                    animate="visible"
                    className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
                >
                    {features.map((feature, index) => (
                        <motion.div
                            key={index}
                            variants={itemVariants}
                            whileHover={{ scale: 1.05, boxShadow: "0 0 20px rgba(0, 243, 255, 0.2)" }}
                            className="bg-gray-900/50 backdrop-blur-md border border-gray-800 p-8 rounded-xl hover:border-neon-blue transition-colors duration-300 group"
                        >
                            <div className="mb-6 bg-gray-800/50 p-4 rounded-lg w-fit group-hover:bg-neon-blue/10 transition-colors">
                                {feature.icon}
                            </div>
                            <h3 className="text-2xl font-bold font-orbitron mb-4 text-white group-hover:text-neon-blue transition-colors">
                                {feature.title}
                            </h3>
                            <p className="text-gray-400 leading-relaxed group-hover:text-gray-300 transition-colors">
                                {feature.description}
                            </p>
                        </motion.div>
                    ))}
                </motion.div>
            </div>
        </div>
    );
};

export default Features;
